<template>
  <v-card>
    <v-card-title class="mb-2">{{ surveyQuestionStatistic.surveyQuestion.text }}</v-card-title>
    <v-card-subtitle>{{ trans('survey.statistic.answeredTotalLong', {answeredTotal: surveyQuestionStatistic.answeredTotal}) }}</v-card-subtitle>
    <v-card-text>
      <v-tabs v-model="currentTab" class="elevation-0">
        <v-tab>{{ trans('general.report') }}</v-tab>
        <v-tab>{{ transPlural('survey.question.answer') }}</v-tab>
        <v-tab v-if="surveyQuestionStatistic.surveyQuestion.rankable">{{ transPlural('survey.question.rankingList') }}</v-tab>
      </v-tabs>
      <v-tabs-items
        v-model="currentTab"
      >
        <v-tab-item>
          <v-row dense>
            <v-col :cols="colsLeft">
              <survey-question-chart
                :survey-question-statistic="surveyQuestionStatistic"
                @click-answer="onAnswerClicked"
              />
            </v-col>
            <v-col :cols="colsRight">
              <survey-question-chart
                :survey-question-statistic="surveyQuestionStatistic"
                chart-type="pie"
                @click-answer="onAnswerClicked"
              />
            </v-col>
          </v-row>
        </v-tab-item>

        <v-tab-item>
          <ajax-table
            :resource="'surveys/questions/' + surveyQuestionStatistic.surveyQuestion.id + '/results'"
            :headers="[
              {text: 'booking.bookingId', value: 'surveyResult.booking.id', maxWidth: '100px'},
              {text: 'object.object', value: 'surveyResult.booking.object.name', maxWidth: '260px'},
              {text: 'survey.question.answer', value: 'text', sortable: !isTextQuestion},
              {text: 'survey.answeredAt', value: 'surveyResult.answeredAt'},
              {text: '', value: 'actions', sortable: false, width: 20},
            ]"
            :page-options="{itemsPerPage: 10}"
            :items-per-page-options="[5, 10, 20]"
            no-elevation
            :filter="surveyQuestionResultFilter"
          >
            <template v-slot:filters>
              <v-col cols="6" sm="6" md="4" lg="3">
                <v-select
                  :items="surveyQuestionAnswersOptions"
                  :label="transSingular('survey.question.answer')"
                  v-model="surveyQuestionResultFilterData.answerId"
                  hide-details
                  clearable
                />
              </v-col>
              <v-col cols="6" sm="6" md="4" lg="3">
                <select-object
                  :object-id.sync="surveyQuestionResultFilterData.objectId"
                  clearable
                  hide-details
                />
              </v-col>
              <v-col cols="6" sm="6" md="4" lg="3">
                <search v-model="surveyQuestionResultFilterData.search" width="auto" />
              </v-col>
            </template>

            <template v-slot:item.surveyResult.booking.id="{ item }">
              <booking-id
                :value="item.surveyResult.bookingId"
                :open-in-new-tab="false"
                :custom-link="{name: 'SurveyResultPage', params: {bookingId: item.surveyResult.bookingId}}"
              />
            </template>

            <template v-slot:item.surveyResult.booking.object.name="{ item }">
              <div style="min-width: 200px;">
                <object-name :value="item.surveyResult" />
              </div>
            </template>
            <template v-slot:item.text="{ item }">
              <span v-if="!isTextQuestion">{{ item.surveyAnswer.text }}</span>
              <text-quote v-else-if="item.text" :text="item.text" :preview-length="160" style="font-size: 0.9em; max-width: 500px;" />
              <span v-else>&ndash;</span>
            </template>
            <template v-slot:item.surveyResult.answeredAt="{ item }">
              <span v-if="item.surveyResult" style="min-width: 150px; display: inline-block;" :title="formatDate(item.surveyResult.answeredAt, 'LLL')">{{ formatDate(item.surveyResult.answeredAt, 'fromNow') }}</span>
            </template>

            <template v-slot:item.actions="{ item }">
              <show-icon-button @click="pushToRoute('SurveyResultPage', {bookingId: item.surveyResult.bookingId})" />
            </template>
          </ajax-table>
        </v-tab-item>

        <v-tab-item v-if="surveyQuestionStatistic.surveyQuestion.rankable">
          <ajax-table
            :resource="'surveys/questions/' + surveyQuestionStatistic.surveyQuestion.id + '/ranking'"
            :headers="[
              {text: 'object.object', value: 'surveyResult.booking.object.name'},
              {text: 'survey.numberOfSurveyResults', value: 'numberOfSurveyResults', width: 250},
              {text: 'survey.ratingAverage', value: 'ratingAverage', width: 300},
              {text: '', value: 'actions', sortable: false, width: 20}
            ]"
            :page-options="{itemsPerPage: 10}"
            :items-per-page-options="[5, 10, 20]"
            :filter="objectRankingsFilter"
            no-elevation
          >
            <template v-slot:filters>
              <v-col cols="6" sm="6" md="4" lg="3">
                <select-object
                  :object-id.sync="objectRankingsFilterData.objectId"
                  clearable
                  hide-details
                />
              </v-col>
              <v-col cols="6" sm="6" md="4" lg="3">
                <search v-model="objectRankingsFilterData.search" width="auto" />
              </v-col>
            </template>

            <template #item.surveyResult.booking.object.name="{ item }">
              <object-name :value="item" />
            </template>

            <template #item.numberOfSurveyResults="{ item }">
              <a
                @click="showResultAnswersForObject(item.objectId)"
                :title="trans('survey.showResultAnswersForObject')"
              >{{ item.numberOfSurveyResults }}</a>
            </template>

            <template #item.ratingAverage="{ item }">
              <div class="d-flex align-center">
                <template v-if="ratingDisplay === 'FIVE_STARS'">
                  <v-rating
                    empty-icon="mdi-star-outline"
                    full-icon="mdi-star"
                    half-icon="mdi-star-half-full"
                    half-increments
                    length="5"
                    dense
                    :value="getScaledRatingAverage(item)"
                  >
                    <template #item="{ isFilled, isHalfFilled }">
                      <v-icon v-if="isHalfFilled" color="orange lighten-2" size="18">mdi-star-half-full</v-icon>
                      <v-icon v-else-if="isFilled" color="orange lighten-2" size="18">mdi-star</v-icon>
                      <v-icon v-else size="18" color="#DDD">mdi-star</v-icon>
                    </template>
                  </v-rating>
                  <div class="ml-3" style="font-size: 0.85em; opacity: 0.8;">{{ formatRatingAverage(getScaledRatingAverage(item)) }} von 5</div>
                </template>
                <template v-else-if="ratingDisplay === 'PERCENT'">
                  <v-progress-linear
                    :value="getScaledRatingAverage(item)"
                    height="6"
                    style="max-width: 100px;"
                    :key="'ratingBar-' + item.objectId"
                    :color="getRatingColor(item)"
                  />
                  <div class="ml-3" style="font-size: 0.85em; opacity: 0.8;">{{ formatRatingAverage(getScaledRatingAverage(item)) }}%</div>
                </template>
              </div>
            </template>

            <template v-slot:item.actions="{ item }">
              <show-icon-button
                @click="showResultAnswersForObject(item.objectId)"
                :title="trans('survey.showResultAnswersForObject')"
              />
            </template>
          </ajax-table>
        </v-tab-item>
      </v-tabs-items>

    </v-card-text>
  </v-card>
</template>

<script>
import {LocaleUtils} from '@/utils/locale-utils';
import {SurveyStatisticCardMixin} from '@/components/survey/statistic/SurveyStatisticCardMixin';
import SurveyQuestionChart from '@/components/survey/statistic/SurveyQuestionChart';
import AjaxTable from '../../AjaxTable';
import {DateUtils} from '../../../utils/date-utils';
import TextQuote from '../TextQuote';
import BookingId from '../../BookingId';
import ObjectName from '../../ObjectName';
import Search from '../../Search';
import {comparison, eq, gt, or} from 'rsql-builder';
import {FilterUtils} from '../../../utils/filter-utils';
import SelectObject from '../../SelectObject';
import {isNull} from '../../../lib/custom-rsql-operators';
import ShowIconButton from '../../ShowIconButton';
import {RouterUtils} from '../../../utils/router-utils';
import {locale} from '../../../plugins/i18n';

export default {
  name: 'SurveyQuestionCard',
  components: {ShowIconButton, SelectObject, Search, ObjectName, BookingId, TextQuote, AjaxTable, SurveyQuestionChart},
  mixins: [
    LocaleUtils,
    SurveyStatisticCardMixin,
    DateUtils,
    FilterUtils,
    RouterUtils
  ],
  props: {
    surveyQuestionStatistic: {
      required: true
    }
  },
  data () {
    return {
      surveyQuestionResultFilterData: {
        search: null,
        objectId: null,
        answerId: null
      },
      objectRankingsFilterData: {
        search: null,
        objectId: null
      },
      currentTab: 0,
      ratingDisplay: 'PERCENT'
    }
  },
  computed: {
    isTextQuestion () {
      return this.surveyQuestionStatistic?.surveyQuestion?.type === 'TEXT';
    },

    surveyQuestionResultFilter () {
      let filters = [];
      if (this.surveyQuestionResultFilterData.answerId != null) {
        if (this.isTextQuestion) {
          if (this.surveyQuestionResultFilterData.answerId) {
            filters.push(comparison('text', gt(0)))
          } else {
            filters.push(or(
              comparison('text', eq('')),
              comparison('text', isNull())
            ))
          }
        } else {
          filters.push(comparison('surveyAnswer.id', eq(this.surveyQuestionResultFilterData.answerId)))
        }
      }
      if (this.surveyQuestionResultFilterData.objectId != null) {
        filters.push(comparison('surveyResult.booking.object.id', eq(this.surveyQuestionResultFilterData.objectId)))
      }
      if (this.surveyQuestionResultFilterData.search) {
        filters.push(this.buildSearchFilter(this.surveyQuestionResultFilterData.search, [
          'surveyResult.booking.id',
          'surveyResult.booking.object.name',
          'surveyResult.booking.object.city',
          'text'
        ]))
      }
      return filters;
    },

    objectRankingsFilter () {
      let filters = [];
      if (this.objectRankingsFilterData.objectId != null) {
        filters.push(comparison('surveyResult.booking.object.id', eq(this.objectRankingsFilterData.objectId)))
      }
      if (this.objectRankingsFilterData.search) {
        filters.push(this.buildSearchFilter(this.objectRankingsFilterData.search, [
          'surveyResult.booking.object.name',
          'surveyResult.booking.object.city'
        ]))
      }
      return filters;
    },

    surveyQuestionAnswersOptions () {
      let options = [];

      if (this.isTextQuestion) {
        options.push(...[
          {text: this.trans('survey.statistic.textAnswersCount'), value: true},
          {text: this.trans('survey.statistic.noTextAnswersCount'), value: false}
        ]);
      } else {
        this.surveyQuestionStatistic?.surveyQuestion?.surveyAnswers?.forEach(surveyAnswer => {
          options.push({
            text: surveyAnswer.text,
            value: surveyAnswer.id
          });
        })
      }

      return options;
    },

    maximumRankingPoints () {
      return this.surveyQuestionStatistic.surveyQuestion.rankable
        ? this.surveyQuestionStatistic.surveyQuestion.surveyAnswers
          .map(surveyAnswer => surveyAnswer.rankingPoints)
          .sort((a, b) => a > b ? -1 : 1)[0]
        : null;
    }
  },
  methods: {
    onAnswerClicked (answerId) {
      if (['ANSWERED', 'NOT_ANSWERED'].includes(answerId)) {
        answerId = answerId === 'ANSWERED';
      }
      this.surveyQuestionResultFilterData.answerId = answerId;
      this.currentTab = 1;
    },

    getScaledRatingAverage (item) {
      return item.ratingAverage * (this.ratingDisplay === 'FIVE_STARS' ? 5 : 100) / this.maximumRankingPoints;
    },

    formatRatingAverage (ratingAverage) {
      return new Intl.NumberFormat(locale, {
        minimumFractionDigits: this.ratingDisplay === 'FIVE_STARS' ? 1 : 0,
        maximumFractionDigits: this.ratingDisplay === 'FIVE_STARS' ? 1 : 0
      }).format(ratingAverage);
    },

    getRatingColor (item) {
      return this.getHexByGradient('#8bc574', '#c0604a', this.getScaledRatingAverage(item) / 100);
    },

    getHexByGradient (colorMax, colorMin, weight) {
      let colorMaxRgb = this.hexToRgb(colorMax);
      let colorMinRgb = this.hexToRgb(colorMin);
      let w1 = weight;
      let w2 = 1 - w1;
      return this.rgbToHex(
        Math.round(colorMaxRgb[0] * w1 + colorMinRgb[0] * w2),
        Math.round(colorMaxRgb[1] * w1 + colorMinRgb[1] * w2),
        Math.round(colorMaxRgb[2] * w1 + colorMinRgb[2] * w2)
      );
    },

    hexToRgb (hex) {
      let result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
      return result ? [
        parseInt(result[1], 16),
        parseInt(result[2], 16),
        parseInt(result[3], 16)
      ] : null;
    },

    rgbToHex (r, g, b) {
      return '#' + this.componentToHex(r) + this.componentToHex(g) + this.componentToHex(b);
    },

    componentToHex (c) {
      let hex = c.toString(16);
      return hex.length === 1 ? '0' + hex : hex;
    },

    showResultAnswersForObject (objectId) {
      this.surveyQuestionResultFilterData.objectId = objectId;
      this.currentTab = 1;
    }
  }
}
</script>

<style scoped>

</style>
